import { useId } from 'react';
import type { CardPlan, CardPlanCatalog } from '../../lib/card-plan-checkout';
import { formatMoney } from '../../lib/money';

type Props = {
  catalog: CardPlanCatalog | null;
  selectedPlan: CardPlan;
  onChange: (plan: CardPlan) => void;
  loading: boolean;
  error: string | null;
  disabled?: boolean;
  onRefresh: () => void;
};

export function CardPlanSelection({ catalog, selectedPlan, onChange, loading, error, disabled, onRefresh }: Props) {
  const id = useId();
  if (loading) return <p role="status">Refreshing subscription terms…</p>;
  if (!catalog) return <div>
    <p role="status">{error ?? 'Subscription terms are unavailable.'}</p>
    <button type="button" onClick={onRefresh} disabled={disabled}>Refresh subscription terms</button>
  </div>;
  if (catalog.existingPlan) return <section className="card-plan-selection" aria-label="Subscription">
    <strong>Existing {catalog.existingPlan.toLowerCase()} plan</strong>
    <p>Your existing plan continues; no new subscription or trial is added to this card purchase.</p>
  </section>;
  const offer = catalog.items.find((item) => item.plan === selectedPlan);
  const interval = offer?.interval;
  const count = offer?.intervalCount;
  const known = offer?.unitAmount != null && offer.currency && interval && count;
  return <section className="card-plan-selection" aria-label="Subscription">
    <label htmlFor={id}>Subscription plan</label>
    <select id={id} value={selectedPlan} onChange={(event) => onChange(event.target.value as CardPlan)} disabled={disabled}>
      {catalog.items.map((item) => <option key={item.plan} value={item.plan}>{item.label}</option>)}
    </select>
    {offer ? <>
      <p><strong>{offer.label} subscription · {catalog.trialDays}-day free trial</strong></p>
      <p>{known
        ? `Then ${formatMoney(offer.unitAmount, offer.currency)} every ${count === 1 ? interval : `${count} ${interval}s`}.`
        : 'Recurring price confirmed at checkout.'}</p>
      <p>The card is a one-time charge. Your subscription renews automatically after the trial unless canceled. Review both line items in Stripe before paying. Manage or cancel your plan in Plan &amp; Billing.</p>
    </> : <p>Choose an available plan before continuing.</p>}
  </section>;
}
